import { computed, type ComputedRef } from 'vue'
import type { PaginationInfo } from '@/types/subscription'

// Constants
const MAX_VISIBLE_PAGES = 7
const PAGES_AROUND_CURRENT = 1

export interface UsePaginationOptions {
  pagination: ComputedRef<PaginationInfo | null> | PaginationInfo | null
  totalCount: ComputedRef<number> | number
  isLoading?: ComputedRef<boolean> | boolean
  onPageChange: (page: number) => void
}

export interface UsePaginationReturn {
  shouldShowPagination: ComputedRef<boolean>
  getStartItem: () => number
  getEndItem: () => number
  getPageNumbers: () => (number | string)[]
  goToPage: (page: number) => void
  goToNextPage: () => void
  goToPreviousPage: () => void
}

/**
 * Composable for pagination logic
 * Handles page number generation, navigation, and display range calculations
 */
export function usePagination(options: UsePaginationOptions): UsePaginationReturn {
  const { pagination, totalCount, isLoading, onPageChange } = options

  // Helper to unwrap refs or return value
  const unwrap = <T>(value: ComputedRef<T> | T): T => {
    return typeof value === 'object' && value !== null && 'value' in value ? value.value : value
  }

  // Computed properties
  const shouldShowPagination = computed(() => {
    const pag = unwrap(pagination)
    return pag ? pag.total_pages > 1 : false
  })

  // Display range calculations
  const getStartItem = (): number => {
    const pag = unwrap(pagination)
    if (!pag) return 1
    return (pag.current_page - 1) * pag.page_size + 1
  }

  const getEndItem = (): number => {
    const pag = unwrap(pagination)
    const total = unwrap(totalCount)
    if (!pag) return total
    const end = pag.current_page * pag.page_size
    return Math.min(end, total)
  }

  // Navigation handlers
  const goToPage = (page: number): void => {
    const loading = isLoading ? unwrap(isLoading) : false
    if (loading) return
    onPageChange(page)
  }

  const goToNextPage = (): void => {
    const pag = unwrap(pagination)
    const loading = isLoading ? unwrap(isLoading) : false
    if (pag?.has_next && !loading) {
      goToPage(pag.current_page + 1)
    }
  }

  const goToPreviousPage = (): void => {
    const pag = unwrap(pagination)
    const loading = isLoading ? unwrap(isLoading) : false
    if (pag?.has_previous && !loading) {
      goToPage(pag.current_page - 1)
    }
  }

  // Page number generation with ellipsis
  const getPageNumbers = (): (number | string)[] => {
    const pag = unwrap(pagination)
    if (!pag) return []

    const { current_page: current, total_pages: total } = pag
    const pages: (number | string)[] = []

    // Show all pages if within max visible limit
    if (total <= MAX_VISIBLE_PAGES) {
      return Array.from({ length: total }, (_, i) => i + 1)
    }

    // Always show first page
    pages.push(1)

    // Determine page range based on current position
    if (current <= 4) {
      // Near start: [1] [2] [3] [4] [5] [...] [last]
      for (let i = 2; i <= 5; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(total)
    } else if (current >= total - 3) {
      // Near end: [1] [...] [total-4] [total-3] [total-2] [total-1] [total]
      pages.push('...')
      for (let i = total - 4; i <= total; i++) {
        pages.push(i)
      }
    } else {
      // Middle: [1] [...] [current-1] [current] [current+1] [...] [last]
      pages.push('...')
      for (let i = current - PAGES_AROUND_CURRENT; i <= current + PAGES_AROUND_CURRENT; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(total)
    }

    return pages
  }

  return {
    shouldShowPagination,
    getStartItem,
    getEndItem,
    getPageNumbers,
    goToPage,
    goToNextPage,
    goToPreviousPage
  }
}
